import type { Alert, AlertLevel } from '../../api/types';

/** Icône (emoji) par code d'alerte (§1.6). */
const ALERT_ICON: Record<string, string> = {
  TC_DEPASSES: '🌡',
  VOIES_DEPASSEES: '💧',
  MOYEN_NON_AUTORISE: '🚫',
  MOYEN_INDISPONIBLE: '🚫',
  CYCLE_NON_AUTORISE: '🚫',
  COMPOSANT_MANQUANT: '⚠',
  COMPOSANT_A_SORTIR: 'ⓘ',
  OP_AMONT_RETARD: '⏱',
  DELAI_COMPROMIS: '📅',
  CONFLIT_OUTILLAGE: '🔧',
};

export function alertIcon(code: string): string {
  return ALERT_ICON[code] ?? '•';
}

const LEVEL_TEXT: Record<AlertLevel, string> = {
  blocking: 'text-red-700',
  warning: 'text-amber-700',
  info: 'text-blue-700',
};

const LEVEL_BG: Record<AlertLevel | 'none', string> = {
  blocking: 'bg-red-50 border-red-200',
  warning: 'bg-amber-50 border-amber-200',
  info: 'bg-blue-50 border-blue-200',
  none: 'bg-gray-50 border-gray-200',
};

export function severityDot(severity: AlertLevel | 'none'): string {
  return severity === 'blocking' ? 'bg-red-500' : severity === 'warning' ? 'bg-amber-500' : severity === 'info' ? 'bg-blue-500' : 'bg-green-500';
}

/** Ligne d'alerte compacte. */
export function AlertLine({ alert }: { alert: Alert }) {
  return (
    <div className={`flex items-start gap-1.5 text-xs ${LEVEL_TEXT[alert.level]}`}>
      <span>{alertIcon(alert.code)}</span>
      <span>{alert.message}</span>
    </div>
  );
}

/** Bloc d'alertes encadré selon la sévérité maximale. */
export function AlertList({ alerts }: { alerts: Alert[] }) {
  if (alerts.length === 0) return null;
  const worst: AlertLevel | 'none' = alerts.some((a) => a.level === 'blocking') ? 'blocking' : alerts.some((a) => a.level === 'warning') ? 'warning' : 'info';
  return (
    <div className={`rounded border px-2 py-1.5 space-y-1 ${LEVEL_BG[worst]}`}>
      {alerts.map((a, i) => (
        <AlertLine key={i} alert={a} />
      ))}
    </div>
  );
}

/** Jauge compacte « utilisé / max » avec bascule de couleur au dépassement. */
export function Gauge({ label, used, max }: { label: string; used: number; max: number }) {
  const ratio = max > 0 ? used / max : 0;
  const over = used > max;
  const color = over ? 'bg-red-500' : ratio > 0.9 ? 'bg-amber-500' : 'bg-green-500';
  return (
    <div className="text-[11px]">
      <div className="flex justify-between text-gray-500">
        <span>{label}</span>
        <span className={over ? 'text-red-600 font-medium' : 'text-gray-700'}>{used}/{max}</span>
      </div>
      <div className="mt-0.5 h-1.5 bg-gray-200 rounded overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${Math.min(100, ratio * 100)}%` }} />
      </div>
    </div>
  );
}
